import React from 'react';
import { Building2, Phone, Scale, User } from 'lucide-react';

import {
  Anchor,
  Button,
  Box,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Code,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { TimestampGitLogo } from './Logo.jsx';
import useConfig from './configProvider.js';

function InfoCard({ icon, title, children }) {
  return (
    <Paper radius="lg" p="lg" withBorder h="100%">
      <Stack gap="sm">
        <Group gap="xs">
          <ThemeIcon variant="light" color="blue" size="md" radius="xl">
            {icon}
          </ThemeIcon>
          <Text fw={700} fz="sm" tt="uppercase" lts={1}>
            {title}
          </Text>
        </Group>
        <Stack gap={2}>{children}</Stack>
      </Stack>
    </Paper>
  );
}

export default function ImprintPage() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const { data: configData } = useConfig();
  const [, setLocation] = useLocation();

  return (
    <Box mih="100vh" py={60}>
      <Container size="md">
        <Stack gap="xl">
          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Paper withBorder radius="md" p="md" style={{ width: 520, maxWidth: '100%' }}>
                <Stack gap="xs">
                  <Box w="100%">
                    <TimestampGitLogo />
                  </Box>
                  <Group gap="xs">
                    <Text size="sm">{t('Version')}:</Text>
                    <Code>{configData?.version || '-'}</Code>
                  </Group>
                  <Group gap="xs" align="flex-start" wrap="nowrap">
                    <Text size="sm">{t('Commit')}:</Text>
                    <Code style={{ overflowWrap: 'anywhere' }}>{configData?.commit || '-'}</Code>
                  </Group>
                </Stack>
              </Paper>
              <Text>
                {t('Mathematical proof, that shows when you implemented each line of your code.')}
              </Text>
              <Text>
                {t('This gives you a way to show, when you came up with an algorithm.')}
              </Text>
            </Stack>
          </Paper>

          <Paper
            withBorder
            radius="xl"
            p="xl"
            bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}
          >
            <Stack align="center" ta="center" gap="xs">
              <Title order={1} fz="2.5rem" fw={800}>
                {t('Legal Notice')}
              </Title>
              <Text c="dimmed" size="lg" tt="uppercase" lts={2} fw={600}>
                {t('Impressum')}
              </Text>
            </Stack>
          </Paper>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            <InfoCard
              icon={<Building2 size={16} />}
              title={t('Company Information')}
            >
              <Text fw={700}>{t('RSJ Software GmbH')}</Text>
              <Text>{t('Holzstrasse 4')}</Text>
              <Text>{t('82110 Germering')}</Text>
              <Text>{t('Germany')}</Text>
            </InfoCard>

            <InfoCard icon={<Phone size={16} />} title={t('Contact')}>
              <Text>
                {t('Phone:')}{' '}
                {t('+49 89 89 41 42-0')}
              </Text>
              <Text>
                {t('Fax:')}{' '}
                {t('+49 89 89 41 42-80')}
              </Text>
              <Text>
                {t('E-Mail:')}{' '}
                <Anchor href="mailto:info@rsj.de">info@rsj.de</Anchor>
              </Text>
              <Text>
                {t('Web:')}{' '}
                <Anchor href="http://www.rsj.de" target="_blank" rel="noopener noreferrer">
                  www.rsj.de
                </Anchor>
              </Text>
            </InfoCard>

            <InfoCard icon={<User size={16} />} title={t('Management')}>
              <Text>{t('Managing Director:')}</Text>
              <Text fw={700}>{t('Ruediger Jungbeck')}</Text>
            </InfoCard>

            <InfoCard icon={<Scale size={16} />} title={t('Legal Registry')}>
              <Text>{t('Trade Register: Munich HRB 76084')}</Text>
              <Text>{t('VAT-ID: DE 128 239 592')}</Text>
              <Text>{t('Tax ID: 117/136/80100')}</Text>
            </InfoCard>
          </SimpleGrid>

          <Paper withBorder radius="md" p="lg">
            <Stack gap="xs">
              <Text>{t('(c) 2026 RSJ Software GmbH. All rights reserved.')}</Text>
              <Group gap="md">
                <Button variant="light" onClick={() => setLocation('/privacy')}>
                  {t('Privacy Policy')}
                </Button>
                <Button variant="light" onClick={() => setLocation('/terms')}>
                  {t('Terms of Service')}
                </Button>
              </Group>
              <Text size="sm" c="dimmed">
                {t('Information according to Sec. 5 TMG (German Telemedia Act). Responsible for content according to Sec. 55 para. 2 RStV: Ruediger Jungbeck.')}
              </Text>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
